import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import {
  Alert,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import API from '../../../../services/API'
import ListReportPDF from '../../../../components/PDF/ListReportPDF'
import PrintButton from '../../../../components/PDF/PrintButton'
import usePrintChrome from '../../../../components/PDF/usePrintChrome'
import { downloadPdf } from '../../../../helpers/downloadPdf'
import { GOOD, BAD, WARN } from '../../../../components/PDF/pdfKit'

const EVENT_LABELS = {
  received: 'Received',
  movedIn: 'Moved in',
  movedOut: 'Moved out',
  administered: 'Administered',
  disposed: 'Disposed',
  destroyed: 'Destroyed',
  drawnUp: 'Drawn up',
  prepDiscarded: 'Syringe discarded',
  prepReturned: 'Syringe returned',
  auditAdded: 'Audit adjustment (+)',
  auditRemoved: 'Audit adjustment (−)',
  correction: 'Correction',
  void: 'Void',
  info: 'Note',
  unknown: 'Unclassified',
}

const Tile = ({ label, value, caption }) => (
  <Grid item xs={6} md={3} sx={{ display: 'flex' }}>
    <Stack
      component={Paper}
      padding={1.5}
      alignItems="center"
      sx={{ display: 'flex', width: '100%' }}
    >
      <Typography variant="h5" fontWeight="bold">
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" textAlign="center">
        {label}
        {caption ? ` — ${caption}` : ''}
      </Typography>
    </Stack>
  </Grid>
)

const CDRegisterLedger = () => {
  const { descriptionID } = useParams()
  const [searchParams] = useSearchParams()
  const location = searchParams.get('location')
  const chrome = usePrintChrome()

  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [ledger, setLedger] = useState(null)

  useEffect(() => {
    if (!location) {
      setFetchError(true)
      setLoading(false)
      return
    }
    API.get(`/medsmx/cd-register/${descriptionID}?location=${location}`)
      .then((res) => {
        if (res.status === 200) {
          setLedger(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [descriptionID, location])

  const personName = (row) =>
    [
      row.administeredBy?.firstName ?? row.person?.firstName,
      row.administeredBy?.lastName ?? row.person?.lastName,
    ]
      .filter(Boolean)
      .join(' ')

  const handlePrint = async () => {
    await downloadPdf(
      <ListReportPDF
        title={`CD Register — ${ledger.drug.title}`}
        subtitle={`${ledger.location.name} · Closing balance: ${ledger.currentBalance}${
          ledger.exactFrom
            ? ` · Balances exact from ${moment(ledger.exactFrom).format('DD/MM/YYYY')}`
            : ''
        }`}
        docLabel="Controlled Drug Register"
        {...chrome}
        orientation="portrait"
        stats={[
          {
            label: 'Current balance',
            value: ledger.currentBalance,
            color: GOOD,
          },
          { label: 'Reserved by draft ePCRs', value: ledger.reservedByEpcr },
          {
            label: 'Drawn up & held',
            value: ledger.preparedFromHere,
            color: ledger.preparedFromHere ? WARN : undefined,
          },
        ]}
        columns={[
          { label: 'Date', flex: 0.9 },
          { label: 'Entry', flex: 0.9 },
          { label: 'In/Out', flex: 0.5, align: 'right' },
          { label: 'Balance', flex: 0.5, align: 'right' },
          { label: 'By', flex: 0.9 },
          { label: 'Witness', flex: 0.9 },
        ]}
        rows={ledger.rows.map((row) => [
          moment(row.date).format('DD/MM/YY HH:mm'),
          row.struck || row.status !== 'active'
            ? {
                pill: {
                  label: `${EVENT_LABELS[row.eventType] ?? row.eventType} (${row.status})`,
                  color: BAD,
                },
              }
            : (EVENT_LABELS[row.eventType] ?? row.eventType),
          row.delta === null || row.delta === undefined
            ? '—'
            : row.delta > 0
              ? `+${row.delta}`
              : String(row.delta),
          row.balance === null || row.balance === undefined
            ? '—'
            : String(row.balance),
          personName(row) || '-',
          row.witnessedBy || '-',
        ])}
        emptyText="No register entries"
      />,
      `AmbuNet_CD_Register_${ledger.drug.title.replace(/[^a-z0-9]+/gi, '_')}_${moment().format('YYYY-MM-DD')}.pdf`,
    )
  }

  return (
    <Stack gap={1}>
      {loading ? (
        <LinearProgress />
      ) : fetchError ? (
        <Alert severity="error">
          Unable to load this register. Please try again later.
        </Alert>
      ) : (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Stack>
              <Typography variant="title2">
                CD Register — {ledger.drug.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ledger.location.name} ({ledger.location.type})
              </Typography>
            </Stack>
            <PrintButton
              onPrint={handlePrint}
              label="Export for inspection"
              variant="outlined"
            />
          </Stack>

          <Grid container spacing={1}>
            <Tile label="Current balance" value={ledger.currentBalance} />
            <Tile
              label="Reserved by draft ePCRs"
              value={ledger.reservedByEpcr}
              caption="in balance, held for in-flight records"
            />
            <Tile
              label="Drawn up & held"
              value={ledger.preparedFromHere}
              caption="left this location as syringes"
            />
            <Tile label="Entries" value={ledger.rows.length} />
          </Grid>

          {ledger.exactFrom && (
            <Alert severity="info">
              Running balances are exact from{' '}
              {moment(ledger.exactFrom).format('Do MMM YYYY')} — older entries
              predate structured register records, so their balances can't be
              reconstructed and show as “—”.
            </Alert>
          )}

          <CustomDataGrid
            rows={ledger.rows}
            getRowId={(params) => params._id}
            disableRowSelectionOnClick
            getRowClassName={({ row }) =>
              row.status !== 'active' ? 'cd-register-struck' : ''
            }
            sx={{
              '& .cd-register-struck': {
                textDecoration: 'line-through',
                opacity: 0.6,
              },
            }}
            norowsoverlay={
              <Alert severity="info">
                No register entries for this drug here yet.
              </Alert>
            }
            slots={{toolbar: false}}
            columns={[
              {
                field: 'date',
                headerName: 'Date',
                minWidth: 150,
                renderCell: ({ row }) =>
                  moment(row.date).format('DD/MM/YY HH:mm'),
                valueGetter: (value) => moment(value).toISOString(),
              },
              {
                field: 'eventType',
                headerName: 'Entry',
                minWidth: 170,
                display: 'flex',
                renderCell: ({ row }) => (
                  <Chip
                    size="small"
                    label={EVENT_LABELS[row.eventType] ?? row.eventType}
                    color={
                      row.status !== 'active'
                        ? 'default'
                        : row.delta > 0
                          ? 'success'
                          : row.delta < 0
                            ? 'error'
                            : 'default'
                    }
                    variant="outlined"
                  />
                ),
              },
              {
                field: 'delta',
                headerName: 'In/Out',
                minWidth: 80,
                align: 'right',
                headerAlign: 'right',
                renderCell: ({ row }) =>
                  row.delta === null || row.delta === undefined
                    ? '—'
                    : row.delta > 0
                      ? `+${row.delta}`
                      : row.delta,
              },
              {
                field: 'balance',
                headerName: 'Balance',
                minWidth: 90,
                align: 'right',
                headerAlign: 'right',
                renderCell: ({ row }) =>
                  row.balance === null || row.balance === undefined ? (
                    '—'
                  ) : (
                    <b>{row.balance}</b>
                  ),
              },
              {
                field: 'person',
                headerName: 'By',
                minWidth: 160,
                valueGetter: (value, row) => personName(row),
              },
              {
                field: 'witnessedBy',
                headerName: 'Witness',
                minWidth: 140,
                valueGetter: (value, row) => row.witnessedBy ?? '',
              },
              {
                field: 'note',
                headerName: 'Details',
                minWidth: 300,
                flex: 1,
              },
            ]}
          />
        </>
      )}
    </Stack>
  )
}

export default CDRegisterLedger
